/**
 * System Health Dashboard - Monitor system status and errors
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Activity, 
  Database, 
  AlertTriangle, 
  CheckCircle, 
  RefreshCw,
  Server,
  Clock,
  AlertCircle,
  XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SystemHealthDashboard = ({ token }) => {
  const [health, setHealth] = useState(null);
  const [errorSummary, setErrorSummary] = useState(null);
  const [recentErrors, setRecentErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch health status
      const healthRes = await fetch(`${API_URL}/api/health`);
      const healthData = await healthRes.json();
      setHealth(healthData);

      // Fetch error summary
      const summaryRes = await fetch(`${API_URL}/api/admin/errors/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setErrorSummary(summaryData);
      }

      // Fetch recent errors
      const errorsRes = await fetch(`${API_URL}/api/admin/errors?hours=24&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (errorsRes.ok) {
        const errorsData = await errorsRes.json();
        setRecentErrors(errorsData.errors || []);
      }

      setLastChecked(new Date());
    } catch (err) {
      console.error('Failed to fetch health:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHealth();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchHealth, 60000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'ok': return 'bg-green-100 text-green-700';
      case 'healthy': return 'bg-green-100 text-green-700';
      case 'warning': return 'bg-yellow-100 text-yellow-700';
      case 'degraded': return 'bg-yellow-100 text-yellow-700';
      case 'error': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ok': 
      case 'healthy': 
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
      case 'degraded':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  if (loading && !health) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6" />
            Estado del Sistema
          </h2>
          {lastChecked && (
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
              <Clock className="h-3 w-3" />
              Última verificación: {lastChecked.toLocaleTimeString()}
            </p>
          )}
        </div>
        <Button onClick={fetchHealth} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Main Status Card */}
      <Card className={`border-l-4 ${health?.status === 'healthy' ? 'border-l-green-500' : 'border-l-yellow-500'}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Server className="h-5 w-5" />
              Estado General
            </CardTitle>
            <Badge className={getStatusColor(health?.status)}>
              {health?.status === 'healthy' ? 'Saludable' : 'Degradado'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {health?.checks && Object.entries(health.checks).map(([key, check]) => (
              <div key={key} className="flex items-center gap-2 p-2 rounded bg-muted/50">
                {getStatusIcon(check.status)}
                <div>
                  <p className="text-sm font-medium capitalize">{key.replace('_', ' ')}</p>
                  <p className="text-xs text-muted-foreground">
                    {check.status === 'ok' ? 'Funcionando' : check.error || check.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Data Counts */}
      {health?.checks?.data?.counts && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Productos</p>
                  <p className="text-2xl font-bold">{health.checks.data.counts.products?.toLocaleString()}</p>
                </div>
                <Database className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pedidos</p>
                  <p className="text-2xl font-bold">{health.checks.data.counts.orders?.toLocaleString()}</p>
                </div>
                <Database className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Clientes</p>
                  <p className="text-2xl font-bold">{health.checks.data.counts.customers?.toLocaleString()}</p>
                </div>
                <Database className="h-8 w-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Usuarios</p>
                  <p className="text-2xl font-bold">{health.checks.data.counts.users?.toLocaleString()}</p>
                </div>
                <Database className="h-8 w-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Error Summary */}
      {errorSummary && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Resumen de Errores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 rounded bg-muted/50">
                <p className="text-2xl font-bold text-green-600">{errorSummary.last_hour || 0}</p>
                <p className="text-sm text-muted-foreground">Última hora</p>
              </div>
              <div className="text-center p-3 rounded bg-muted/50">
                <p className="text-2xl font-bold text-blue-600">{errorSummary.last_24h || 0}</p>
                <p className="text-sm text-muted-foreground">Últimas 24h</p>
              </div>
              <div className="text-center p-3 rounded bg-muted/50">
                <p className="text-2xl font-bold text-purple-600">{errorSummary.last_7d || 0}</p>
                <p className="text-sm text-muted-foreground">Últimos 7 días</p>
              </div>
              <div className="text-center p-3 rounded bg-muted/50">
                <p className={`text-2xl font-bold ${errorSummary.unresolved > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {errorSummary.unresolved || 0}
                </p>
                <p className="text-sm text-muted-foreground">Sin resolver</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Errors */}
      {recentErrors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Errores Recientes (24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentErrors.slice(0, 5).map((error, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded bg-red-50 border border-red-100">
                  <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{error.error_type}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(error.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm mt-1">{error.error_message}</p>
                    {error.endpoint && (
                      <p className="text-xs text-muted-foreground mt-1">Endpoint: {error.endpoint}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Errors Message */}
      {recentErrors.length === 0 && (
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center gap-3 text-green-700">
              <CheckCircle className="h-8 w-8" />
              <div>
                <p className="font-medium">¡Sin errores recientes!</p>
                <p className="text-sm opacity-80">No se han registrado errores en las últimas 24 horas.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SystemHealthDashboard;
